import classNames from "classnames"
import { Children, ReactNode, forwardRef, isValidElement, useRef } from "react"
import { mergeRefs } from "../utils/mergeRefs"
import { TABLE_CELL_CLASSES } from "./constants"
import {
  TableProps,
  TableBodyProps,
  TableCellProps,
  TableHeadProps,
  TableFootProps,
  TableControlsProps,
} from "./types"
import { TableHeader } from "./TableHeader"
import { TableFilterButton } from "./TableFilterButton"
import { TableSearchInput } from "./TableSearchInput"
import { TableActionButtonsContainer, TableActionButton } from "./TableActionButtons"
import { TableContextProvider, useTableContext } from "./TableContext"
import { TableSelectionMessage } from "./selection/TableSelectionMessage"
import { TableRowSelectMenu } from "./selection/TableRowSelectMenu"
import { isTableEmpty } from "./isTableEmpty"
import { useFixedColumnsStyles } from "./useFixedColumnsStyles"
import { TableBulkActionsBar } from "./selection/TableBulkActionsBar"
import { defaultOptionsConfig } from "./selection/multiRowSelectDefaultOptionsConfig"
import { TableRow } from "./TableRow"
import { LoadingSpinner } from "../LoadingSpinner"

function getContainsSearchAsFirstChild(children: ReactNode) {
  const firstChild = Children.toArray(children)[0]
  return (
    isValidElement(firstChild) &&
    (firstChild.type as { displayName?: string }).displayName === "Table.SearchInput"
  )
}

export const TableControls = forwardRef<HTMLDivElement, TableControlsProps>(
  ({ className, children, ...rest }, ref) => {
    // only needed on mount - so useRef is fine
    const containsSearch = useRef(getContainsSearchAsFirstChild(children))
    const _className = classNames(
      "grid grid-flow-col grid-cols-[minmax(0,1fr),auto] gap-3 px-2 py-3 shadow-[inset_0px_-1px_0px] shadow-gray-200 md:gap-4",
      className,
    )

    // Regarding grid cols: we always need to define the second column auto so that we do not rely on an implicit grid to place the mobile filter button
    // This ensures that, on mobile, the buttons always span the full grid, while the filter button takes up the space of the
    return (
      <div className={_className} ref={ref} {...rest}>
        {
          /**
           * Ensures that the first grid cell is always reserved for the input.
           * When there is no input, this empty div is added instead so that the buttons take up the rest of the row through the implicit grid
           */
          !containsSearch.current && <div />
        }
        {children}
      </div>
    )
  },
)

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ children, ...rest }, ref) => {
    const { isLoading } = useTableContext()

    if (isLoading) {
      return null
    }

    return (
      <tbody ref={ref} {...rest}>
        {children}
      </tbody>
    )
  },
)
TableBody.displayName = "Table.Body"

export const TableHead = forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ children, className, ...rest }, ref) => {
    const _className = classNames(className, "bg-gray-100 text-black")
    return (
      <thead ref={ref} className={_className} {...rest}>
        {children}
      </thead>
    )
  },
)
TableHead.displayName = "Table.Head"

export const TableFoot = forwardRef<HTMLTableSectionElement, TableFootProps>(
  ({ children, className, ...rest }, ref) => {
    return (
      <tfoot ref={ref} className={className} {...rest}>
        {children}
      </tfoot>
    )
  },
)
TableFoot.displayName = "Table.Foot"

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ children, className, ...rest }, ref) => {
    const { className: fixedColumnsClasses, style } = useFixedColumnsStyles()
    const _className = classNames(TABLE_CELL_CLASSES, fixedColumnsClasses, className)

    return (
      <td ref={ref} className={_className} style={style} {...rest}>
        {children}
      </td>
    )
  },
)
TableCell.displayName = "Table.Cell"

const TableInner = forwardRef<
  HTMLTableElement,
  Omit<
    TableProps,
    | "multiRowSelectOptionsConfig"
    | "fixedStartColumn"
    | "multiSelectionState"
    | "isSelectable"
    | "onChangeMultiSelectionState"
  >
>(
  (
    {
      afterTable,
      afterColumnHeaders = null,
      bulkActionsBar,
      beforeTable,
      caption,
      children,
      className,
      fixedLayout,
      loadingTitle = "Loading",
      style,
      tableClassName,
      scrollContainerRef,
      ...rest
    },
    ref,
  ) => {
    const {
      onTableContainerScroll,
      tableContainerRef: contextTableContainerRef,
      isLoading,
      lastLoadedTableContainerHeight,
    } = useTableContext()

    const isEmpty = isTableEmpty(children)
    const scrollContainerClassName = classNames(
      isLoading || isEmpty ? "overflow-hidden" : "overflow-auto",
      className,
    )
    const _className = classNames(
      "flex flex-col border border-gray-200 rounded-md overflow-clip", // Hide corners which spill over rounded borders
      !beforeTable && "border-t-0",
      className,
    )

    const _tableClassName = classNames(
      "min-w-[100%] bg-white",
      fixedLayout && "table-fixed",
      tableClassName,
    )

    return (
      <>
        <div className={_className}>
          {beforeTable}
          <div
            className={scrollContainerClassName}
            ref={mergeRefs(contextTableContainerRef, scrollContainerRef || null)}
            onScroll={onTableContainerScroll}
            style={{
              // When loading, we maintain the table height
              height:
                isLoading && lastLoadedTableContainerHeight
                  ? lastLoadedTableContainerHeight
                  : undefined,
            }}
          >
            {/* Relatively positioned element which manages the position of the afterColumnHeaders */}
            {/* Set isolate since children use z-index when fixedStartColumn is defined */}
            <div className="relative isolate w-max min-w-[100%]">
              <table className={_tableClassName} ref={ref} {...rest}>
                {caption && <caption className="sr-only">{caption}</caption>}
                {children}
              </table>
              {afterColumnHeaders}
            </div>
            <div
              role="status"
              aria-live="polite"
              className={classNames(isLoading && "h-full w-full")}
            >
              {isLoading && (
                <div className="grid h-full min-h-40 place-items-center" id="spin">
                  <LoadingSpinner className="h-10 w-10" title={loadingTitle} />
                </div>
              )}
            </div>
          </div>
          {afterTable}
        </div>
        {bulkActionsBar}
      </>
    )
  },
)

/**
 * Renders `<Table/>`. Use Table.Body, Table.Cell etc. Read Storybook documentation for information on sizing table columns
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      fixedEndColumn,
      fixedStartColumn,
      isLoading,
      isSelectable = false,
      multiRowSelectOptionsConfig = defaultOptionsConfig,
      multiSelectionState,
      onChangeMultiSelectionState,
      ...rest
    },
    ref,
  ) => {
    return (
      <TableContextProvider
        isLoading={isLoading}
        fixedEndColumn={fixedEndColumn}
        fixedStartColumn={fixedStartColumn}
        isSelectable={isSelectable}
        onChangeMultiSelectionState={onChangeMultiSelectionState}
        multiRowSelectOptionsConfig={multiRowSelectOptionsConfig}
        multiSelectionState={multiSelectionState}
      >
        <TableInner ref={ref} {...rest} />
      </TableContextProvider>
    )
  },
) as TableComponent

Table.displayName = "Table"

interface TableStatics {
  /**
   * Container for ActionButtons
   */
  ActionButtonsContainer: typeof TableActionButtonsContainer
  /**
   * Button for an action which relates to the table entries
   */
  ActionButton: typeof TableActionButton
  /**
   * Renders `<tbody/>`
   */
  Body: typeof TableBody
  /**
   * Component to show actions to carry out when multiple records are selected
   */
  BulkActionsBar: typeof TableBulkActionsBar
  /**
   * Renders `<td/>` -- table data cell
   */
  Cell: typeof TableCell
  /**
   * Element to place before Table. Use it when you need to add a search input or actions above the table
   */
  Controls: typeof TableControls
  /**
   * Renders `<thead/>`
   */
  Head: typeof TableHead
  /**
   * Renders `<th/>` -- table header cell
   */
  Header: typeof TableHeader
  /**
   * Renders `<tfoot/>`
   */
  Foot: typeof TableFoot
  /**
   * Button element specifically for table component filter feature
   */
  FilterButton: typeof TableFilterButton
  /**
   * Renders `<tr/>`
   */
  Row: typeof TableRow
  /**
   * Input specifically for a table component
   */
  SearchInput: typeof TableSearchInput
  /**
   * Element which manages the select all functionality of the table records.
   * Renders a button which looks like a checkbox
   */
  RowSelectMenu: typeof TableRowSelectMenu
  /**
   * Renders a message at the top of the table, below the column headers.
   */
  SelectionMessage: typeof TableSelectionMessage
}
type TableComponent = React.ForwardRefExoticComponent<TableProps> & TableStatics

Table.Header = TableHeader
Table.Row = TableRow
Table.Head = TableHead
Table.FilterButton = TableFilterButton
Table.Foot = TableFoot
Table.Cell = TableCell
Table.Body = TableBody
Table.Controls = TableControls
Table.SearchInput = TableSearchInput
Table.ActionButton = TableActionButton
Table.ActionButtonsContainer = TableActionButtonsContainer
Table.RowSelectMenu = TableRowSelectMenu
Table.SelectionMessage = TableSelectionMessage
Table.BulkActionsBar = TableBulkActionsBar
